import { type FunctionComponent, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { BlockType, useCompliancePrompt } from '../../../sessions/hooks/use-compliance-prompt';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { useFullscreen } from '../../../shared/hooks/use-fullscreen';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { GameFrame } from '../../components/game-frame/game-frame';
import { GameNavigationBar } from '../../components/game-navigation-bar/game-navigation-bar';
import { Toolbar } from '../../components/toolbar/toolbar';
import { useGameLaunch } from '../../hooks/use-game-launch';
import { gameplayStore } from '../../store/gameplay.store';
import { InGameSearchContainer } from '../ingame-search/ingame-search';

import styles from './gameplay.module.scss';

const style = createStyleHelper(styles, 'gameplay');

export interface IGameplayProps {
    slugs: Array<string | undefined>;
    onSlugsChange: (slugs: Array<string | undefined>) => void;
    onClose: () => void;
}

export const Gameplay: FunctionComponent<IGameplayProps> = ({ slugs, onSlugsChange, onClose }) => {
    const { slots, updateSlotCount } = useGameLaunch({
        initialSlugs: slugs,
        onSlugsChange,
    });

    const isDesktop = useDesktop();
    const dispatch = useDispatch();
    const hasMultipleSlots = slots.length > 1;
    const { setFullscreen, isFullscreen } = useFullscreen();
    const onCloseSelected = useCallback(() => {
        if (isFullscreen) {
            setFullscreen(false);
        }

        onClose();
    }, [onClose, setFullscreen, isFullscreen]);

    const toggleSearch = useCallback(
        (slotIndex: number) => {
            dispatch(gameplayStore.actions.toggleSearch({ slot: slotIndex }));
        },
        [dispatch],
    );

    const onToggleSearch = useCallback(() => {
        if (slots.length === 1) {
            toggleSearch(0);
        } else if (slots.length === 2) {
            toggleSearch(0);
            toggleSearch(1);
        }
    }, [slots, toggleSearch]);

    const vendors = useMemo(
        () =>
            slots.map(({ game }) => {
                return game?.gameVendor.id || '';
            }),
        [slots],
    );

    const currentGamesIds = useMemo(
        () =>
            slots.map(({ internalGameId }) => {
                return internalGameId ?? '';
            }),
        [slots],
    );

    const replaceSlot = useCallback(
        (slotIndex: number, slug: string) => {
            dispatch(gameplayStore.actions.setSlotSlug({ slot: slotIndex, slug }));
        },
        [dispatch],
    );

    const isSearchOpen = slots
        .map(({ isSearchOpen }) => {
            return isSearchOpen;
        })
        .every((v) => v === true);

    const { prompt } = useCompliancePrompt(BlockType.WAGER);

    return (
        <div className={style()}>
            {prompt}
            {isDesktop && (
                <div className={style('nav', { inline: hasMultipleSlots })}>
                    <Toolbar
                        className={style('toolbar', { inline: hasMultipleSlots })}
                        onClose={onCloseSelected}
                        onSlotCountChange={updateSlotCount}
                        activeSlotCount={slots.length}
                        setFullscreen={setFullscreen}
                        isFullscreen={isFullscreen}
                        toggleSearch={toggleSearch}
                        isSearchActive={slots.length === 1 && slots[0].isSearchOpen}
                    />
                </div>
            )}
            <div
                className={style('matrix', {
                    'has-2': slots.length === 2,
                    'has-4': slots.length > 2,
                })}
            >
                {slots.map(({ game, launchUrl, hasError, errorCode, isSearchOpen }, i) => (
                    <GameFrame
                        className={style('frame', { 'pad-top': !hasMultipleSlots && isDesktop })}
                        key={i}
                        hasError={hasError}
                        errorCode={errorCode}
                        name={game?.name ?? ''}
                        aspectRatio={game?.desktopAspectRatio}
                        launchUrl={launchUrl}
                        showSearchIcon={!!launchUrl && hasMultipleSlots}
                        onSearch={() => toggleSearch(i)}
                    >
                        {isSearchOpen ? (
                            <InGameSearchContainer
                                onSelectGame={(slug) => replaceSlot(i, slug)}
                                currentVendors={vendors}
                                currentGames={currentGamesIds}
                            />
                        ) : null}
                    </GameFrame>
                ))}
            </div>
            {!isDesktop && (
                <GameNavigationBar
                    toggleSearch={onToggleSearch}
                    updateSlotCount={updateSlotCount}
                    isSearchOpen={isSearchOpen}
                />
            )}
        </div>
    );
};
